import React, { useState, useCallback, useMemo, useEffect } from "react";
import { FaMobile, FaShieldAlt, FaCheckCircle, FaExclamationTriangle, FaSpinner, FaArrowRight, FaLock } from 'react-icons/fa';

const MpesaForm = ({ 
  paymentMethods, 
  setOrderData,
  orderData, 
  subTotal, 
  shippingCost, 
  onMethodSelection,
  onClose 
}) => {
  const [selectedCountryCode, setSelectedCountryCode] = useState("+254");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationState, setValidationState] = useState('idle'); // idle, valid, invalid

  // Memoize country codes to prevent unnecessary re-renders
  const countryCodes = useMemo(() => [
    { name: "Kenya", code: "+254", flag: "🇰🇪", placeholder: "712345678" },
    { name: "Uganda", code: "+256", flag: "🇺🇬", placeholder: "701234567" },
  ], []);

  const totalAmount = subTotal + shippingCost;

  // Enhanced phone number validation
  const validatePhoneNumber = useCallback((number, countryCode) => {
    if (countryCode === "+254") {
      // Kenya: Must start with 7 and be 9 digits total
      const kenyanPhoneRegex = /^7\d{8}$/;
      return kenyanPhoneRegex.test(number);
    } else if (countryCode === "+256") {
      // Uganda: Must start with 7 and be 9 digits total
      const ugandanPhoneRegex = /^7\d{8}$/;
      return ugandanPhoneRegex.test(number);
    }
    return false;
  }, []);

  // Real-time phone validation with visual feedback
  useEffect(() => {
    if (phoneNumber.length === 0) {
      setValidationState('idle');
      setError('');
    } else if (phoneNumber.length >= 9) {
      const isValid = validatePhoneNumber(phoneNumber, selectedCountryCode);
      setValidationState(isValid ? 'valid' : 'invalid');
      if (!isValid) {
        setError("Please enter a valid phone number format");
      } else {
        setError('');
      }
    } else {
      setValidationState('invalid');
      setError('');
    }
  }, [phoneNumber, selectedCountryCode, validatePhoneNumber]);

  // Get full phone number for display and processing
  const getFullPhoneNumber = useCallback(() => {
    return `${selectedCountryCode.replace("+", "")}${phoneNumber}`;
  }, [selectedCountryCode, phoneNumber]);

  // Handle phone number input with real-time formatting
  const handlePhoneNumberChange = (e) => {
    const value = e.target.value.replace(/\D/g, ''); // Remove non-digit characters
    if (value.length <= 9) { // Limit to 9 digits
      setPhoneNumber(value);
    }
  };

  const handleCountryCodeChange = (e) => {
    const newCode = e.target.value;
    setSelectedCountryCode(newCode);
    // Clear error when country changes
    setError('');
    setValidationState('idle');
  };

  // Handle form submission
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (!validatePhoneNumber(phoneNumber, selectedCountryCode)) {
      setError("Please enter a valid phone number (e.g., 712345678)");
      setIsSubmitting(false);
      return;
    }

    setError("");

    try {
      const fullPhoneNumber = getFullPhoneNumber();
      
      // Update order data with payment details
      setOrderData((prevData) => ({
        ...prevData,
        paymentDetails: [
          {
            paymentID: 1, // Assuming Mpesa is payment method ID 1
            paymentReference: fullPhoneNumber, // Full phone number for Daraja API
            phoneNumber: fullPhoneNumber, // Full phone number for display
            paymentMethod: "Mpesa", // Hardcoded since this is the Mpesa form
            amount: totalAmount,
          },
        ],
      }));

      onMethodSelection(false);
    } catch (err) {
      setError("Failed to process payment. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }, [phoneNumber, selectedCountryCode, subTotal, shippingCost, setOrderData, onMethodSelection, validatePhoneNumber, getFullPhoneNumber, totalAmount]);

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Header with gradient background */}
      <div className="bg-gradient-to-r from-green-600 to-green-700 text-white p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="bg-white/20 backdrop-blur-sm rounded-lg p-2">
              <FaMobile className="text-2xl" />
            </div>
            <div>
              <h3 className="text-xl font-bold">M-Pesa Payment</h3>
              <p className="text-green-100">Secure mobile payment</p>
            </div>
          </div>
          <div className="flex items-center space-x-2 bg-white/20 backdrop-blur-sm rounded-lg px-3 py-1">
            <FaShieldAlt className="text-green-200" />
            <span className="text-sm font-medium">Secured</span>
          </div>
        </div>
      </div>

      {/* Amount Summary */}
      <div className="bg-gray-50 border-b border-gray-200 p-4">
        <div className="flex justify-between items-center">
          <span className="text-gray-600">Total Amount:</span>
          <div className="text-right">
            <span className="text-2xl font-bold text-gray-900">
              KSH {totalAmount.toLocaleString('en-KE')}
            </span>
            <p className="text-sm text-gray-500">
              Items: KSH {subTotal.toLocaleString('en-KE')} + Delivery: KSH {shippingCost.toLocaleString('en-KE')}
            </p>
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className="p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Instructions */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-blue-800 mb-2 flex items-center">
              <FaMobile className="mr-2" />
              How to pay with M-Pesa:
            </h4>
            <ol className="text-sm text-blue-700 space-y-1">
              <li>1. Enter your M-Pesa registered phone number</li>
              <li>2. Click "Proceed to Pay"</li>
              <li>3. Check your phone for the M-Pesa prompt</li>
              <li>4. Enter your M-Pesa PIN to complete payment</li>
            </ol>
          </div>

          {/* Country Selection */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Country/Region
            </label>
            <select
              value={selectedCountryCode}
              onChange={handleCountryCodeChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white"
              disabled={isSubmitting}
            >
              {countryCodes.map((country) => (
                <option key={country.code} value={country.code}>
                  {country.flag} {country.name} ({country.code})
                </option>
              ))}
            </select>
          </div>

          {/* Phone Number Input */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              M-Pesa Phone Number
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <span className="text-gray-500 font-medium">{selectedCountryCode}</span>
              </div>
              <input
                type="tel"
                value={phoneNumber}
                onChange={handlePhoneNumberChange}
                placeholder={countryCodes.find(c => c.code === selectedCountryCode)?.placeholder || "712345678"}
                className={`w-full pl-20 pr-12 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 transition-all text-lg font-mono ${
                  validationState === 'valid' ? 'border-green-500 bg-green-50' :
                  validationState === 'invalid' ? 'border-red-500 bg-red-50' :
                  'border-gray-300 focus:border-green-500'
                }`}
                disabled={isSubmitting}
                maxLength={9}
              />
              <div className="absolute inset-y-0 right-0 pr-4 flex items-center">
                {validationState === 'valid' && (
                  <FaCheckCircle className="text-green-500" />
                )}
                {validationState === 'invalid' && phoneNumber.length >= 9 && (
                  <FaExclamationTriangle className="text-red-500" />
                )}
              </div>
            </div>
            {error && (
              <p className="text-red-600 text-sm flex items-center mt-2">
                <FaExclamationTriangle className="mr-2" />
                {error}
              </p>
            )}
            {validationState === 'valid' && (
              <p className="text-green-600 text-sm flex items-center mt-2">
                <FaCheckCircle className="mr-2" />
                Valid phone number format
              </p>
            )}
          </div>

          {/* Submit Button */}
          <div className="pt-4">
            <button
              type="submit"
              disabled={isSubmitting || validationState !== 'valid'}
              className={`w-full py-3 px-4 rounded-lg font-semibold text-white transition-all duration-200 shadow-md
                ${isSubmitting || validationState !== 'valid'
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 transform hover:scale-105 hover:shadow-lg'}
              `}
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing Payment...
                </span>
              ) : (
                `Pay KSH ${totalAmount.toLocaleString('en-KE')}`
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Security Notice */}
      <div className="p-6 bg-gray-50 border-t border-gray-200">
        <div className="flex items-start gap-3">
          <FaLock className="text-green-600 mt-1" />
          <div className="text-sm text-gray-700">
            <p className="font-semibold mb-1">🔒 Secure Payment Processing</p>
            <p>Your payment is protected by advanced encryption. We never store your M-Pesa PIN or sensitive information.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MpesaForm;